import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDateTimeToLocaleString as useDateTimeWithLocaleImported } from '@hooks/useDateTimeWithLocale'
import { DateTime } from 'luxon'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { twMerge } from '@lib/tailwind-merge'
import { generateCalendar } from './utils/generateCalendar'
import { Label } from 'components/form/label'
import { Radio } from 'components/form/radio/Radio'
import { getWeekModeStartEnd } from './utils/getStartEnd'

const Button: FC<{
  onClick: () => void
  direction: 'back' | 'forward'
  title: string
}> = ({ onClick, direction, title }) => (
  <button
    className="w-8 h-8 rounded bg-neutral-100 border-neutral-400 [&>svg]:text-neutral-900 border-[1.5px]"
    onClick={onClick}
    title={title}
  >
    <FontAwesomeIcon
      icon={direction === 'back' ? faChevronLeft : faChevronRight}
      size="xs"
    />
  </button>
)

const RadioOption: FC<{
  value: 'sunday' | 'monday'
  checked: boolean
  onChange: () => void
  label: string
}> = ({ checked, onChange, label }) => (
  <Label className="gap-x-2 flex flex-row items-center text-neutral-900 text-md cursor-pointer">
    <Radio checked={checked} onChange={onChange} />
    {label}
  </Label>
)

interface WeekSelectorProps {
  selectedWeek: DateTime
  onWeekSelect: (options: {
    start: DateTime
    weekStartsOn: 'sunday' | 'monday'
  }) => void
}

interface SelectedStartAndEnd {
  start?: DateTime
  end?: DateTime
}

interface AbitraryStartAndProps {
  selected: SelectedStartAndEnd
  onSelect: (options: { start?: DateTime; end?: DateTime }) => void
}

type Props = {
  className?: string
  hideWeekStartsOnSelector?: boolean
  initiallyDisplayedMonth: DateTime
  useDateTimeToLocaleString?: typeof useDateTimeWithLocaleImported
  weekStartsOn: 'monday' | 'sunday'
} & (AbitraryStartAndProps | WeekSelectorProps)

export const MonthCalendar: FC<Props> = ({
  className,
  hideWeekStartsOnSelector = false,
  initiallyDisplayedMonth,
  weekStartsOn: weekStartsOnDefault,
  useDateTimeToLocaleString = useDateTimeWithLocaleImported, // injecting dependency in this way for storybook
  ...props
}) => {
  const { t } = useTranslation()
  const toLocaleString = useDateTimeToLocaleString()
  const [weekStartsOn, setWeekStartsOnOverride] = useState(weekStartsOnDefault)

  const [displayedMonth, setDisplayedMonth] = useState<DateTime>(
    initiallyDisplayedMonth.startOf('month'),
  )

  const getStartEnd = (
    overrides: { selectedWeek: DateTime } | { selected: SelectedStartAndEnd },
    weekStartsOnOverride = weekStartsOn,
  ) => {
    const { start, end } =
      'selectedWeek' in overrides
        ? getWeekModeStartEnd({
            selectedWeek: overrides.selectedWeek,
            weekStartsOn: weekStartsOnOverride,
          })
        : { start: overrides.selected.start, end: overrides.selected.end }

    return { start, end }
  }

  const { start, end } = getStartEnd(props)

  const goBackOneMonth = () => {
    setDisplayedMonth((month) => month.minus({ month: 1 }))
  }

  const goForwardOneMonth = () => {
    setDisplayedMonth((month) => month.plus({ month: 1 }))
  }

  const daysOfWeek = [
    t('common.dayOfWeek.sunday.abr'),
    t('common.dayOfWeek.monday.abr'),
    t('common.dayOfWeek.tuesday.abr'),
    t('common.dayOfWeek.wednesday.abr'),
    t('common.dayOfWeek.thursday.abr'),
    t('common.dayOfWeek.friday.abr'),
    t('common.dayOfWeek.saturday.abr'),
  ]

  if (weekStartsOn === 'monday') {
    const sunday = daysOfWeek.shift() // Remove Sunday from the start

    if (!sunday) throw new Error('Unreachable, Sunday should always be present')

    daysOfWeek.push(sunday) // Move Sunday to the end
  }

  /**
   * In order to reduce cyclical updates, I'm creating a function to compute
   * the start and end dates based on the current props and "weekStartsOn"
   * dynamically.  This is helpful when the user changes the day the week
   * starts on.
   */
  const handleWeekStartsOnChange = (
    weekStartsOnOverride: 'sunday' | 'monday',
  ) => {
    setWeekStartsOnOverride(weekStartsOnOverride)
    const { start, end } = getStartEnd(props, weekStartsOnOverride)

    if ('onSelect' in props) return props.onSelect({ start, end })
    if (!start)
      throw new Error('Unreachable. start date is missing for week selection')

    props.onWeekSelect({ start, weekStartsOn: weekStartsOnOverride })
  }

  return (
    <div className={twMerge('flex-col', className)}>
      <div className="flex flex-row items-center justify-between px-4 pt-4">
        <Button
          onClick={() => goBackOneMonth()}
          direction="back"
          title={t('components.weekSelector.goToPreviousMonth')}
        />
        <span className="text-sm font-semibold text-neutral-900">
          {toLocaleString({ month: 'long', year: 'numeric' })(displayedMonth)}
        </span>
        <Button
          onClick={() => goForwardOneMonth()}
          direction="forward"
          title={t('components.weekSelector.goToNextMonth')}
        />
      </div>
      <div className="flex flex-row items-center mt-5 mb-3 px-4">
        {daysOfWeek.map((dayOfWeek) => (
          <span
            className="flex-1 text-xs text-center uppercase text-neutral-500"
            key={dayOfWeek}
          >
            {dayOfWeek}
          </span>
        ))}
      </div>
      <div className="flex flex-col px-4 pb-4">
        {generateCalendar({ fromDateTime: displayedMonth, weekStartsOn }).map(
          (week) => {
            return (
              <div
                className={twMerge(
                  'flex flex-row overflow-hidden text-neutral-900',
                  'selectedWeek' in props && 'group rounded',
                )}
                key={week[0].toISODate()}
                onClick={() =>
                  'onWeekSelect' in props &&
                  props.onWeekSelect({ start: week[0], weekStartsOn })
                }
              >
                {week.map((day) => {
                  const isWithinDisplayMonth = day.hasSame(
                    displayedMonth,
                    'month',
                  )
                  const isStartOfSelected = start && day.hasSame(start, 'day')
                  const isEndOfSelected = end && day.hasSame(end, 'day')
                  const isAfterStart = start === undefined || day > start
                  const isBeforeEnd = end === undefined || day < end

                  return (
                    <button
                      data-testId={
                        isWithinDisplayMonth
                          ? 'inside-current-month'
                          : 'outside-current-month'
                      }
                      className={twMerge(
                        'flex flex-row items-center justify-center flex-1 w-8 h-8 text-sm',
                        !isWithinDisplayMonth && 'text-neutral-400',
                        isAfterStart &&
                          isBeforeEnd &&
                          'font-semibold text-primary-700 bg-primary-300 rounded-none',
                        (isStartOfSelected || isEndOfSelected) &&
                          'bg-primary-600 font-semibold text-white',
                        isStartOfSelected && !isEndOfSelected && 'rounded-l',
                        isEndOfSelected &&
                          !isStartOfSelected &&
                          'rounded-l-none rounded-r',
                        'selectedWeek' in props &&
                          'first-of-type:group-hover:bg-neutral-300 last-of-type:group-hover:bg-neutral-300 group-hover:text-neutral-900 group-hover:bg-neutral-150',
                      )}
                      key={day.toISODate()}
                      onClick={() => {
                        if (!('onSelect' in props)) return
                        const select = ({
                          start,
                          end,
                        }: {
                          start?: DateTime
                          end?: DateTime
                        }) =>
                          props.onSelect({
                            start: start?.startOf('day'),
                            end: end?.endOf('day'),
                          })

                        if (start && start.hasSame(day, 'day')) {
                          select({ start: day, end: day })
                        } else if (end && end.hasSame(day, 'day')) {
                          select({ start: day, end: day })
                        } else if (start && !end) {
                          if (day < start) {
                            select({ start: day, end: start })
                          } else {
                            select({ start, end: day })
                          }
                        } else if (end && !start) {
                          if (day < end) {
                            select({ start: day, end })
                          } else {
                            select({ start: end, end: day })
                          }
                        } else if (!start && !end) {
                          select({ start: day })
                        } else if (start && end) {
                          if (day <= start) {
                            select({ start: day, end })
                          } else {
                            select({ start, end: day })
                          }
                        } else {
                          select({ start })
                        }
                      }}
                    >
                      {day.day}
                    </button>
                  )
                })}
              </div>
            )
          },
        )}
      </div>
      {!hideWeekStartsOnSelector && (
        <div className="flex flex-col bg-neutral-100 border-t border-neutral-200 px-4 py-2 gap-2">
          <span className="text-neutral-500 font-semibold uppercase text-xs">
            {t('components.monthCalendar.weekStart')}
          </span>
          <div className="flex flex-row">
            {(['sunday', 'monday'] as const).map((day) => (
              <RadioOption
                key={day}
                value={day}
                checked={weekStartsOn === day}
                onChange={() => handleWeekStartsOnChange(day)}
                label={t(`common.dayOfWeek.${day}.full`)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
